import React, { useEffect, useState } from 'react';
import { useLocale } from 'hooks';
import { Grid, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useFormContext } from 'react-hook-form';

import { StepContainer, Checkbox, IconTile } from 'ui';
import { GenericField, ScanField } from 'form/fields';
import { CheckboxIcon, CheckboxIconBorders, SendIcon } from 'ui/icons';
import { UploadField } from 'form/fields/uploadField/UploadField';

import { useStyles } from './StepFormInstaller.styles';
import { StepFormInstallerProps } from './StepFormInstaller.types';

export const StepFormInstaller = ({ goToNextStep, onSubmit }: StepFormInstallerProps) => {
  const { watch, setValue } = useFormContext();
  const [acceptedAgreement, setAcceptedAgreement] = useState(false);
  const { tileContainer, container, sendIcon, labelSubtitle, typographyContainer } = useStyles();
  const { formatMessage } = useLocale();
  const theme = useTheme();

  const eanCode = watch('id');

  useEffect(() => {
    const handleSuccess = (location: Position) => {
      setValue('location', {
        latitude: Number(location.coords.latitude.toFixed(6)),
        longitude: Number(location.coords.longitude.toFixed(6)),
      });
    };
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(handleSuccess);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = () => {
    onSubmit();
    goToNextStep();
  };

  const canReportInstallation = acceptedAgreement && eanCode;

  return (
    <StepContainer title={formatMessage({ id: 'verify_installation.formTitle' })}>
      <Grid container spacing={1} className={container}>
        <Grid item xs={12}>
          <ScanField
            onScan={value => setValue('id', value)}
            label={formatMessage({ id: 'verify_installation.id' })}
            name="id"
          />
        </Grid>
        <Grid item xs={12}>
          <ScanField
            onScan={value => setValue('meterSerial', value)}
            label={formatMessage({ id: 'install_battery.meter_id' })}
            name="meterSerial"
          />
        </Grid>
        <Grid item xs={12}>
          <GenericField label={formatMessage({ id: 'install_battery.address' })} name="address" />
        </Grid>
        <Grid item xs={6}>
          <GenericField label={formatMessage({ id: 'install_battery.latitude' })} name="location.latitude" />
        </Grid>
        <Grid item xs={6}>
          <GenericField label={formatMessage({ id: 'install_battery.longitude' })} name="location.longitude" />
        </Grid>
        <Grid item xs={12}>
          <UploadField
            label={formatMessage({ id: 'install_battery.installation_photo' })}
            name="installationPhoto"
            compressImage
          />
        </Grid>
      </Grid>
      <Grid container className={typographyContainer} spacing={1}>
        <Grid container item xs={12} direction="row" alignItems="center">
          <Checkbox
            icon={<CheckboxIconBorders />}
            checkedIcon={<CheckboxIcon />}
            color="primary"
            label={formatMessage({ id: 'activateAsset.steps.installationAgreement' })}
            checked={acceptedAgreement}
            onChange={() => setAcceptedAgreement(prevState => !prevState)}
          />
          <Typography className={labelSubtitle}>
            {formatMessage({ id: 'activateAsset.steps.installationLegalNotice' })}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={1} className={tileContainer}>
        <Grid container item xs={12} justify="center">
          <IconTile
            backgroundColor={canReportInstallation ? theme.palette.secondary.main : theme.palette.gray.dark}
            color={theme.palette.text.primary}
            title={formatMessage({ id: 'activateAsset.steps.reportInstallation' })}
            icon={<SendIcon classes={{ root: sendIcon }} />}
            onClick={canReportInstallation ? handleSubmit : () => null}
          />
        </Grid>
      </Grid>
    </StepContainer>
  );
};
